import React from 'react';
import PropTypes from 'prop-types';

import i18n from '../../../i18n';

import Home from '../../../models/Home';
import User from '../../../models/User';

import { Collapsible, Callout, List, Text, Link, Flex, Icon, Textarea } from '@homeexchange/design';
import { getLastSentStayRequestMessage } from '@homeexchange/conversations';
import useGTM from '../../analytics/useGTM';
import Analytics from '../../../utils/analytics';
import { NAVIGATION_ACTION, NAVIGATION_AREAS, NAVIGATION_TEXT } from '../../analytics/analytics-constants';
class _MessageField extends React.PureComponent {
    static propTypes = {
        user: PropTypes.instanceOf(User).isRequired,
        home: PropTypes.instanceOf(Home).isRequired,
        exchangeType: PropTypes.number.isRequired,
        conversationsStats: PropTypes.boolean,
        hasError: PropTypes.boolean,
        onChange: PropTypes.func,
        message: PropTypes.string.isRequired
    };

    constructor(props) {
        super(props);
        this.state = {
            messagePrewritten: false,
            lastMessage: null
        };
        this.messageChange = this.messageChange.bind(this);
        this.getPreWrittenMessage = this.getPreWrittenMessage.bind(this);
        this.switchMessage = this.switchMessage.bind(this);
        this.shouldDisplayMessageTemplate = this.shouldDisplayMessageTemplate.bind(this);
        this.onTrackNavigationButton = this.onTrackNavigationButton.bind(this);
        this.handleOnCopyLastMessage = this.handleOnCopyLastMessage.bind(this);
        this.getLastMessage = this.getLastMessage.bind(this);
    }

    componentDidUpdate(prevProps) {
        const { exchangeType, onChange } = this.props;
        if (prevProps.exchangeType !== exchangeType && this.state.messagePrewritten) {
            onChange(this.getPreWrittenMessage());
        }
    }

    componentDidMount() {
        this.getLastMessage();
    }

    messageChange(e) {
        const { onChange } = this.props;

        if (e) {
            const { value } = e.target;
            const message = value ? value : '';

            onChange(message);
        }
    }

    getPreWrittenMessage() {
        const { user, home } = this.props;

        return i18n.t('exchange:page.exchangeRequest.message.input.template', {
            HostName: home.get('user').get('first_name'),
            GuestName: user.get('first_name')
        });
    }

    switchMessage(event) {
        event.preventDefault();
        const { onChange } = this.props;
        const { messagePrewritten } = this.state;
        let message = '';

        if (!messagePrewritten) {
            message = this.getPreWrittenMessage();
        }

        this.setState(
            {
                messagePrewritten: !messagePrewritten
            },
            () => {
                $('#content').trigger('change');
            }
        );

        this.onTrackNavigationButton(NAVIGATION_TEXT.USE_A_TEMPLATE_MESSAGE);
        onChange(message);
    }

    shouldDisplayMessageTemplate() {
        const { conversationsStats } = this.props;
        return conversationsStats['nb_conversations_created_by_user'] < 15;
    }

    onTrackNavigationButton(text) {
        const { fetchNavigation } = useGTM(this.props.user);
        Analytics.trackGTM(
            'button',
            fetchNavigation(NAVIGATION_ACTION.CLICK, NAVIGATION_AREAS.CONTACT_MODAL, text)
        );
    }

    getLastMessage() {
        const { home } = this.props;

        const receiverName = home.get('user').get('first_name');
        const location = home.firstAdmin();

        getLastSentStayRequestMessage(encodeURIComponent(receiverName), encodeURIComponent(location)).then(
            (data) => {
                const message = data.stayRequestMessage;
                this.props.onChange(message);
                this.setState({ lastMessage: message });
            }
        );
    }

    handleOnCopyLastMessage(event) {
        const { onChange } = this.props;
        const { lastMessage } = this.state;

        event.preventDefault();

        onChange(lastMessage);
        this.onTrackNavigationButton(NAVIGATION_TEXT.COPY_MY_LAST_REQUEST);
    }

    render() {
        const { home, hasError, message } = this.props;
        const { lastMessage } = this.state;

        return (
            <Flex className="message-field" direction="column" gap="1">
                <Flex alignItems="center" gap="3">
                    <Icon name="Pencil" />
                    <Text color={hasError ? 'error' : 'default'} isBold>
                        {i18n.t('exchange:page.exchangeRequest.message.input.label')}
                    </Text>
                </Flex>

                <Text
                    dangerouslySetInnerHTML={{
                        __html: i18n.t('exchange:page.exchangeRequest.message.input.helper', {
                            hostName: home.get('user').get('first_name')
                        })
                    }}
                />

                <Collapsible
                    className="message-field-collapsible"
                    labelClosed={i18n.t('exchange:page.exchangeRequest.tips.collapsible.title')}
                    labelOpen={i18n.t('exchange:page.exchangeRequest.tips.collapsible.title')}
                    openOn="bottom"
                    isHighlighted={true}
                    onChange={(isOpen) => {
                        if (isOpen) {
                            this.onTrackNavigationButton(NAVIGATION_TEXT.TIPS_FROM_OUR_COMMUNITY);
                        }
                    }}
                >
                    <List
                        elements={[
                            {
                                title: i18n.t('exchange:page.exchangeRequest.tips.collapsible.title1'),
                                text: i18n.t('exchange:page.exchangeRequest.tips.collapsible.body1')
                            },
                            {
                                title: i18n.t('exchange:page.exchangeRequest.tips.collapsible.title2'),
                                text: i18n.t('exchange:page.exchangeRequest.tips.collapsible.body2')
                            },
                            {
                                title: i18n.t('exchange:page.exchangeRequest.tips.collapsible.title3'),
                                text: i18n.t('exchange:page.exchangeRequest.tips.collapsible.body3')
                            }
                        ]}
                    />
                </Collapsible>

                <Flex className="message-field-text-area-container" gap="2" direction="column">
                    <Textarea
                        isFullWidth
                        status={hasError ? 'error' : null}
                        value={message}
                        onChange={this.messageChange}
                        onBlur={this.messageChange}
                        placeholder={i18n.t('exchange:page.exchangeRequest.message.input.placeholder')}
                        message={i18n.t('common:validity_valueMissing')}
                    />
                    {lastMessage && (
                        <Link color="secondary" href="#" onClick={this.handleOnCopyLastMessage}>
                            {i18n.t('exchange:page.exchangeRequest.message.lastMessage.cta')}
                        </Link>
                    )}
                </Flex>

                {this.shouldDisplayMessageTemplate() && (
                    <Callout>
                        <Text isBold>
                            {i18n.t('exchange:page.exchangeRequest.message.input.callout.title')}
                        </Text>
                        <Text>{i18n.t('exchange:page.exchangeRequest.message.input.callout.body')}</Text>
                        <Link onClick={this.switchMessage}>
                            {i18n.t('exchange:page.exchangeRequest.message.input.callout.cta')}
                        </Link>
                    </Callout>
                )}
            </Flex>
        );
    }
}

export default _MessageField;
